import React from "react";
import { assets, programByTab, type TrackTab } from "../../content/site";

type ProgramRowProps = (typeof programByTab)[TrackTab][number];

type ProgramTalkGroup = {
    time: string;
    place?: string;
    floor?: string;
    talks: ProgramRowProps[];
};

type ProgramRowRenderProps = ProgramRowProps | ProgramTalkGroup;

function isTalkGroup(item: ProgramRowRenderProps): item is ProgramTalkGroup {
    return "talks" in item;
}

function renderSpeaker(speaker: NonNullable<ProgramRowProps["speakers"]>[number], index: number) {
    return (
        <div key={`${speaker.name}-${index}`} className="flex items-start gap-4 max-lg:gap-3">
            <div className="grid h-[64px] w-[64px] shrink-0 place-items-center overflow-hidden rounded-full bg-accent max-lg:h-[56px] max-lg:w-[56px] max-lg:rounded-[1205.96px]">
                <img
                    src={speaker.avatar ?? assets.avatar}
                    alt=""
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                />
            </div>
            <div className="min-w-0 min-h-[64px] flex flex-col justify-center max-w-[390px] max-lg:max-w-[185px]">
                <h3 className="mb-1 text-[28px] leading-[1.05] font-medium tracking-[-0.04em] text-white max-lg:text-[16px] max-lg:font-bold max-lg:uppercase">
                    {speaker.name}
                </h3>
                <p className="m-0 text-[18px] leading-[1.15] font-normal tracking-[-0.04em] text-muted max-lg:text-[13px] max-lg:leading-[1.1]">
                    {speaker.role}
                </p>
            </div>
        </div>
    );
}

function renderTalkContent(item: ProgramRowProps, talkIndex?: number) {
    const resolvedNote =
        item.note ??
        (item.speakers?.some((speaker) => speaker.name === "Борис Харанутов")
            ? "*Roblox заблокирован на территории РФ"
            : undefined);

    return (
        <div
            key={talkIndex ?? item.topic}
            className={[
                "grid gap-8 lg:grid-cols-[390px_421px]",
                talkIndex !== undefined && talkIndex > 0
                    ? "border-t border-muted/60 pt-8 max-lg:pt-6"
                    : "",
                "max-lg:gap-6",
            ].join(" ")}
        >
            <div className="flex flex-col gap-5 pt-1 max-lg:gap-3 max-lg:pt-0">
                {item.speakers?.map(renderSpeaker)}
            </div>
            <div className="flex flex-col gap-5 pt-1 max-lg:gap-3 max-lg:pt-0">
                <strong className="max-w-[453px] whitespace-pre-line text-[24px] font-bold leading-none tracking-[-0.04em] text-white max-lg:text-[16px] max-lg:leading-[1.24] max-lg:font-normal">
                    {item.topic}
                </strong>
                {resolvedNote ? (
                    <p className="m-0 max-w-[453px] text-[14px] leading-[1.4] font-normal tracking-[-0.02em] text-white/45 max-lg:text-[13px]">
                        {resolvedNote}
                    </p>
                ) : null}
            </div>
        </div>
    );
}

export function ProgramRow(item: ProgramRowRenderProps) {
    if (isTalkGroup(item)) {
        return (
            <article className="grid gap-8 border-b border-muted py-10 lg:grid-cols-[222px_926px] lg:gap-[252px] max-lg:rounded-[24px] max-lg:border max-lg:border-muted max-lg:bg-black/95 max-lg:p-[14px] max-lg:py-[14px] max-lg:gap-6">
                <div className="flex max-w-[222px] flex-col justify-center pt-1 max-lg:max-w-none max-lg:pt-0">
                    <strong className="text-[32px] font-bold leading-[1.2] tracking-[-0.04em] uppercase text-white max-lg:text-[20px] max-lg:leading-[1.2]">
                        {item.time}
                    </strong>
                </div>
                <div className="flex flex-col gap-8 max-lg:gap-6">
                    {item.talks.map((talk, index) => renderTalkContent(talk, index))}
                </div>
            </article>
        );
    }

    const resolvedNote =
        item.note ??
        (item.speakers?.some((speaker) => speaker.name === "Борис Харанутов")
            ? "*Roblox заблокирован на территории РФ"
            : undefined);

    return (
        <article
            className={[
                "grid gap-8 border-b border-muted py-10 lg:grid-cols-[222px_926px] lg:gap-[252px]",
                "max-lg:rounded-[24px] max-lg:border max-lg:border-muted max-lg:bg-black/95 max-lg:p-[14px] max-lg:py-[14px]",
                item.break ? "max-lg:gap-3" : "max-lg:min-h-[265px] max-lg:gap-6",
            ].join(" ")}
        >
            <div className="flex max-w-[222px] flex-col justify-center pt-1 max-lg:max-w-none max-lg:pt-0">
                <strong className="text-[32px] font-bold leading-[1.2] tracking-[-0.04em] uppercase text-white max-lg:text-[20px] max-lg:leading-[1.2]">
                    {item.time}
                </strong>
            </div>
            <div
                className={[
                    "grid gap-8 lg:grid-cols-[390px_421px]",
                    item.break ? "max-lg:gap-0" : "max-lg:gap-6",
                ].join(" ")}
            >
                {item.break ? null : (
                    <div className="flex flex-col gap-5 pt-1 max-lg:gap-3 max-lg:pt-0">
                        {item.speakers?.map(renderSpeaker)}
                    </div>
                )}
                <div
                    className={[
                        "flex flex-col gap-5 pt-1 max-lg:gap-3 max-lg:pt-0",
                        item.break ? "pt-0 lg:col-span-2 max-lg:pt-0" : "",
                    ].join(" ")}
                >
                    {item.break ? (
                        <div className="text-[32px] font-bold leading-[1.08] tracking-[-0.04em] text-accent max-lg:text-[20px] max-lg:leading-[1.2]">
                            {item.topic}
                        </div>
                    ) : (
                        <>
                            <strong className="max-w-[453px] whitespace-pre-line text-[24px] font-bold leading-none tracking-[-0.04em] text-white max-lg:text-[16px] max-lg:leading-[1.24] max-lg:font-normal">
                                {item.topic}
                            </strong>
                            {resolvedNote ? (
                                <p className="m-0 max-w-[453px] text-[14px] leading-[1.4] font-normal tracking-[-0.02em] text-white/45 max-lg:text-[13px]">
                                    {resolvedNote}
                                </p>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </article>
    );
}
